import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Pencil, Save, Loader2, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * GardePharmacyEditor — Quick correction tool for pharmacies de garde records.
 * Lets an admin pick a pharmacy from the current garde document and fix its
 * location/name/phones without having to re-upload the whole PDF.
 */
const GardePharmacyEditor = () => {
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedName, setSelectedName] = useState('');
  const [form, setForm] = useState({ name: '', manager: '', location: '', phones: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem('dermascan_token');

  const fetchGarde = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/pharmacy/garde`);
      setPharmacies(res.data?.pharmacies || []);
    } catch (e) {
      toast.error('Erreur lors du chargement des pharmacies de garde');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGarde();
  }, [fetchGarde]);

  useEffect(() => {
    if (!selectedName) {
      setForm({ name: '', manager: '', location: '', phones: '' });
      return;
    }
    const p = pharmacies.find((x) => (x.name || '').trim() === selectedName.trim());
    if (p) {
      setForm({
        name: p.name || '',
        manager: p.manager || '',
        location: p.location || '',
        phones: Array.isArray(p.phones) ? p.phones.join(', ') : (p.phones || ''),
      });
    }
  }, [selectedName, pharmacies]);

  const handleSave = async () => {
    if (!selectedName) return;
    setSaving(true);
    try {
      const payload = {
        name: selectedName,
        new_name: form.name && form.name.trim() !== selectedName.trim() ? form.name.trim() : undefined,
        manager: form.manager,
        location: form.location,
        phones: form.phones.split(',').map((s) => s.trim()).filter(Boolean),
      };
      // Strip undefined
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      await axios.patch(`${API_URL}/pharmacy/garde/pharmacy`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success('Pharmacie mise à jour');
      // Refresh list + keep selection on new name if renamed
      await fetchGarde();
      if (payload.new_name) setSelectedName(payload.new_name);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/30">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-amber-900 text-base">
          <Pencil className="w-4 h-4" />
          Corriger une pharmacie de garde
        </CardTitle>
        <p className="text-xs text-amber-800 mt-1">
          Utile pour réparer une coquille d'adresse sans re-téléverser le PDF. Les modifications
          prennent effet immédiatement sur la bannière d'accueil et la page Garde.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Pharmacie</Label>
          <Select
            value={selectedName || 'none'}
            onValueChange={(v) => setSelectedName(v === 'none' ? '' : v)}
            disabled={loading}
          >
            <SelectTrigger data-testid="garde-edit-pharmacy-select">
              <SelectValue placeholder={loading ? 'Chargement…' : `Choisir une pharmacie (${pharmacies.length})`} />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="none">— Aucune —</SelectItem>
              {pharmacies.map((p) => (
                <SelectItem key={p.name} value={p.name}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedName && (
          <>
            <div>
              <Label>Nom</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="garde-edit-name"
              />
            </div>
            <div>
              <Label>Gérant</Label>
              <Input
                value={form.manager}
                onChange={(e) => setForm({ ...form, manager: e.target.value })}
              />
            </div>
            <div>
              <Label className="flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Adresse (location)
              </Label>
              <Textarea
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                rows={2}
                data-testid="garde-edit-location"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Astuce : en modifiant l'adresse, la vérification GPS est réinitialisée et
                refaite automatiquement en arrière-plan.
              </p>
            </div>
            <div>
              <Label>Téléphones (séparés par des virgules)</Label>
              <Input
                value={form.phones}
                onChange={(e) => setForm({ ...form, phones: e.target.value })}
                placeholder="27 22 30 05 72, 07 78 41 52 77"
              />
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="garde-edit-save"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GardePharmacyEditor;
